import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, FileText, X, CheckCircle, AlertCircle } from 'lucide-react';
import { showToast } from '../utils/toast';
import {
  MAX_FILE_SIZE_MB,
  ALLOWED_DOCUMENT_TYPES,
  ALLOWED_DOCUMENT_EXTENSIONS,
  validateFile
} from '../constants/fileUpload';

const AgendaUploadModal = ({
  open,
  onClose,
  onFileSelect,
  existingFiles = [] // Array of already selected files
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  // Use centralized constants
  const allowedTypes = ALLOWED_DOCUMENT_TYPES;
  const allowedExtensions = ALLOWED_DOCUMENT_EXTENSIONS;

  const getFileTypeIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('excel') || fileType.includes('sheet')) return '📊';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📽️';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('text')) return '📃';
    return '📁';
  };

  const handleFileValidation = (file) => {
    const validation = validateFile(file, allowedTypes);
    if (!validation.isValid) {
      showToast.error(validation.message);
      return false;
    }
    return true;
  };

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];

    fileArray.forEach(file => {
      if (handleFileValidation(file)) {
        // Check if file is already selected (by name and size)
        const isDuplicate = selectedFiles.some(
          f => f.name === file.name && f.size === file.size
        ) || existingFiles.some(
          f => f.name === file.name && f.size === file.size
        );

        if (!isDuplicate) {
          validFiles.push(file);
        } else {
          showToast.error(`File "${file.name}" is already selected`);
        }
      }
    });

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleConfirm = () => {
    if (selectedFiles.length === 0) {
      showToast.error('Please select at least one file');
      return;
    }
    onFileSelect(selectedFiles);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setDragActive(false);
    onClose();
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const allFiles = [...existingFiles, ...selectedFiles];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] w-[95vw]  max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload Agenda Documents
          </DialogTitle>
          <DialogDescription>
            Upload one or more document files for the meeting agenda
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File Upload Area */}
          <div className="space-y-2">
            <Label>Select Files (Multiple files allowed)</Label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
                }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="space-y-3">
                <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                <div>
                  <p className="text-sm font-medium">Drop files here or click to browse</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supported: PDF, Excel, PowerPoint, Word, Images (JPG, PNG), Text
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Max size: {MAX_FILE_SIZE_MB}MB per file</p>
                </div>
                <Input
                  type="file"
                  onChange={handleFileInputChange}
                  accept={allowedExtensions.join(',')}
                  className="hidden"
                  id="agenda-file-upload"
                  multiple
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('agenda-file-upload').click()}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Choose Files
                </Button>
              </div>
            </div>
          </div>

          {/* Selected Files List */}
          {allFiles.length > 0 && (
            <div className="space-y-2">
              <Label>Selected Files ({allFiles.length})</Label>
              <div className="max-h-28  flex flex-wrap items-center gap-2 overflow-y-auto  border rounded-lg p-3 bg-gray-50">
                {allFiles.map((file, index) => {
                  const isExisting = index < existingFiles.length;
                  return (
                    <div
                      key={`${file.name}-${file.size}-${index}`}
                      className="flex  items-center gap-2 p-2 bg-white rounded border"
                    >
                      <span className="text-lg">{getFileTypeIcon(file.type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate max-w-[120px] sm:max-w-[200px]" title={file.name}>
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      {!isExisting && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index - existingFiles.length)}
                          className="text-red-500 hover:text-red-700 flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                      {isExisting && (
                        <span className="text-xs text-gray-400 flex-shrink-0">Existing</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* File Requirements */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Supported File Types:</p>
                <ul className="text-xs space-y-1">
                  <li>• Documents: PDF, DOC, DOCX, TXT</li>
                  <li>• Spreadsheets: XLS, XLSX</li>
                  <li>• Presentations: PPT, PPTX</li>
                  <li>• Images: JPG, JPEG, PNG</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedFiles.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {selectedFiles.length > 0
              ? `Attach ${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}`
              : 'Attach Files'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgendaUploadModal;

